export type Lang = "ne" | "en";

const devanagariDigits = "०१२३४५६७८९";

/** Replaces ASCII digits with Devanagari ones: "2083" → "२०८३". */
export function toNepaliDigits(s: string): string {
  return s.replace(/[0-9]/g, (d) => devanagariDigits[Number(d)]);
}

/** Replaces Devanagari digits with ASCII ones, for input typed on a Nepali keyboard: "९८४१" → "9841". */
export function toAsciiDigits(s: string): string {
  return s.replace(/[०-९]/g, (d) => String(devanagariDigits.indexOf(d)));
}

/** Groups an integer string the South Asian way: "123456" → "1,23,456". */
function groupLakh(digits: string): string {
  if (digits.length <= 3) return digits;

  const parts = [digits.slice(-3)];
  let rest = digits.slice(0, -3);
  while (rest.length > 2) {
    parts.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest) parts.unshift(rest);

  return parts.join(",");
}

/**
 * Formats a number with lakh grouping and up to maxFraction decimals (at least
 * minFraction), in Devanagari digits for Nepali. Done by hand because browsers'
 * Intl data often lacks the Nepali locale.
 */
export function formatNumber(value: number, lang: Lang, { minFraction = 0, maxFraction = 2 } = {}): string {
  const negative = value < 0;
  let [int, frac = ""] = Math.abs(value).toFixed(maxFraction).split(".");
  while (frac.length > minFraction && frac.endsWith("0")) frac = frac.slice(0, -1);

  const s = (negative ? "-" : "") + groupLakh(int) + (frac ? `.${frac}` : "");

  return lang === "ne" ? toNepaliDigits(s) : s;
}

const prefix: Record<Lang, string> = { ne: "रु", en: "Rs" };

/** Formats an amount in paisa as rupees: 75600 → "Rs 756"; 370 → "Rs 3.70"; "रु ७५६" in Nepali. */
export function formatPaisa(paisa: bigint | number, lang: Lang): string {
  const p = Number(paisa);
  const fraction = p % 100 === 0 ? 0 : 2;

  return `${prefix[lang]} ${formatNumber(p / 100, lang, { minFraction: fraction, maxFraction: fraction })}`;
}
